// ===========================================
// Project Detail Modal (Phase 7)
// ===========================================
// Shows project details from the CRM perspective,
// including portal notes and AI summary capability.

import React, { useState, useEffect } from 'react';
import {
  X,
  ExternalLink,
  Sparkles,
  Loader2,
  FileText,
  DollarSign,
  Calendar,
  Building2,
  Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import type { Project } from '@/types';

interface Props {
  project: Project;
  onClose: () => void;
}

interface PortalNote {
  id: string;
  text: string;
  author: string;
  createdAt: string;
  type?: string;
}

export function ProjectDetailModal({ project, onClose }: Props) {
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [isSendingNote, setIsSendingNote] = useState(false);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // AI Summary
  const handleAISummary = async () => {
    if (!project.portalProjectCode) {
      toast.error('AI summary requires a portal project');
      return;
    }

    setIsSummarizing(true);
    try {
      const callable = httpsCallable<any, { summary: string }>(functions, 'acrbs_aiSummarize');
      const result = await callable({
        contentType: 'project_notes',
        content: `Project: ${project.portalProjectCode} - ${project.customerName || project.name}\nType: ${project.projectType}\nStatus: ${project.status}\n\nPlease provide a summary suitable for briefing a referral partner on the status of this project. Focus on: current status, next steps, any delays or issues, and estimated timeline.`,
        customPrompt: 'Summarize this for a sales rep to brief their referral partner. Keep it concise and professional. Focus on project status, not financial details.',
        projectContext: {
          projectCode: project.portalProjectCode || project.name,
          customerName: project.customerName || 'Unknown',
          projectType: project.projectType || 'Unknown',
        },
      });
      setSummary(result.data.summary);
    } catch (err: any) {
      console.error('AI summary error:', err);
      toast.error(err.message || 'Failed to generate summary');
    } finally {
      setIsSummarizing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {project.portalProjectCode || project.name}
            </h2>
            {project.customerName && (
              <p className="text-sm text-gray-600">{project.customerName}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Status & Type */}
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              {project.status}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {project.projectType}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              {project.business}
            </span>
          </div>

          {/* Key Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <DollarSign size={14} />
                Contract Value
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(project.contractAmount)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <DollarSign size={14} />
                Current Total
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(project.totalRevenue)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Calendar size={14} />
                Contract Signed
              </div>
              <p className="font-semibold text-gray-900">{formatDate(project.contractSignedDate)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Calendar size={14} />
                Close Date
              </div>
              <p className="font-semibold text-gray-900">{formatDate(project.closeDate)}</p>
            </div>
          </div>

          {/* Referral Info */}
          {project.referralSourceName && (
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-600 text-xs mb-1">
                <Building2 size={14} />
                Referral Source
              </div>
              <p className="font-medium text-blue-900">{project.referralSourceName}</p>
              {project.referralContactName && (
                <p className="text-sm text-blue-700">Contact: {project.referralContactName}</p>
              )}
            </div>
          )}

          {/* AI Summary */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">AI Summary</h3>
              <button
                onClick={handleAISummary}
                disabled={isSummarizing}
                className="btn btn-secondary text-xs py-1 px-2.5 flex items-center gap-1"
              >
                {isSummarizing ? (
                  <>
                    <Loader2 className="animate-spin" size={12} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={12} />
                    Generate Summary
                  </>
                )}
              </button>
            </div>
            {summary ? (
              <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-900 whitespace-pre-wrap">
                {summary}
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Click "Generate Summary" to get an AI-powered project summary for briefing referral partners.
              </p>
            )}
          </div>

          {/* Open in Portal */}
          {project.portalProjectUrl && (
            <a
              href={project.portalProjectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              <ExternalLink size={16} />
              Open in Portal
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
